import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { Hardware, HardwareType } from "~/types/Hardware";
import { HardwareQuery, edgeService, hardwareService, radioService } from "~/services";
import { ServiceInstance } from "~/types/serviceInstance";
import { Radio } from "~/types/Radio";
import { Edge } from "~/types/Edge";
import { serviceInstanceData } from "./serviceInstanceSlice";
import { groupEdgeCounts, groupRadioCounts } from "~/helpers/summaryCalculations";

export interface HardwareX extends Hardware {
    tenantId?: string;
    serviceInstanceId?: string;
    hardwareType: HardwareType;
    status?: string;
    tenantName:string;
    serviceInstanceName:string;
}



type initState = {
    import: {
        open: boolean;
        data: any[];
        filteredData: any[];
        dataCount: number;
        filteredDataCount: number;
        preserveSign: boolean;
    };
    loading: boolean;
    current: HardwareX | null;
    data: HardwareX[];
    open: boolean;
    radios: Radio[];
    edges: Edge[];
    radiosAndEdgesFetchedCount: number;
    initialDataLoaded: boolean;
}

const initialState: initState = {
    import: {
        open: false,
        data: [],
        filteredData: [],
        dataCount: 0,
        filteredDataCount: 0,
        preserveSign: true,
    },
    loading: false,
    current: null,
    data: [],
    open: false,
    radios: [],
    edges: [],
    radiosAndEdgesFetchedCount: 0,
    initialDataLoaded: false,
}

// Create optimized lookup maps for faster access
function createLookupMaps(serviceInstances: ServiceInstance[]) {
    const tenantMap = new Map<string, string>(); // tenantId -> tenantName
    const serviceInstanceMap = new Map<string, { tenantId: string; name: string }>(); // serviceInstanceId -> { tenantId, name }

    serviceInstances.forEach((instance) => {
        tenantMap.set(instance.tenantId, instance.tenantName);
        serviceInstanceMap.set(instance.id, {
            tenantId: instance.tenantId,
            name: instance.name,
        });
    });

    return { tenantMap, serviceInstanceMap };
}

// TODO: Remove this function and should be coming from the backend
// Transform Hardware to HardwareX with optimized lookups 
function transformHardwareToHardwareX(
    hardware: Hardware[],
    tenantMap: Map<string, string>,
    serviceInstanceMap: Map<string, { tenantId: string; name: string }>
): HardwareX[] {
    return hardware.map((item) => {
        const hardwareX: HardwareX = {
            ...item,
            tenantName: item.tenantId ? (tenantMap.get(item.tenantId) || "") : "",
            serviceInstanceName: item.serviceInstanceId ? (serviceInstanceMap.get(item.serviceInstanceId)?.name || "") : "",
        };
        return hardwareX;
    });
}

// Strip PEM cert/key material and per-edge IPSec config the console never
// reads - at fleet scale these dominate the payload and pin tens of MB in
// Redux across fiber generations (MCS-4316)
const stripRadioSecrets = (radio: Radio): Radio => {
    const { caCert, ipsecCert, ipsecKey, edgeConfig, ...rest } = radio;
    return rest;
};

// Fetch radios only on initial load
export const fetchRadios = createAsyncThunk<Radio[]>(
    `hardware/fetchRadios`,
    async () => {
        try {
            const radios = await radioService.getRadios();
            return radios.map(stripRadioSecrets);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }
);

export const fetchEdges = createAsyncThunk<Edge[]>(
    `hardware/fetchEdges`,
    async () => {
        try {
            return await edgeService.getEdges();
        } catch (error) {
            console.error(error);
            throw error;
        }
    }
);

export const fetchHardwares = createAsyncThunk<
    HardwareX[],
    { payload: HardwareQuery },
    { state: RootState }
>(`hardware/fetchHardwares`, async ({ payload }, { getState }) => {
    try {
        const hardwareData = await hardwareService.getHardwares(payload);
        
        const serviceInstances = serviceInstanceData(getState());
        const { tenantMap, serviceInstanceMap } = createLookupMaps(serviceInstances);
        
        return transformHardwareToHardwareX(
            hardwareData,
            tenantMap,
            serviceInstanceMap
        );
    } catch (error) {
        console.error(error);
        throw error;
    }
});

const hardwareSlice = createSlice({
    name: "hardware",
    initialState,
    reducers: {
        setOpenImport: (state, action: PayloadAction<boolean>) => {
            state.import.open = action.payload;
        },
        setImportData: (state, action: PayloadAction<any[]>) => {
            state.import.data = action.payload;
            state.import.dataCount = action.payload?.length;
        },

        setPreserveSign: (state, action: PayloadAction<boolean>) => {
            state.import.preserveSign = action.payload;
        },

        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setCurrent: (state, action: PayloadAction<HardwareX | null>) => {
            state.current = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRadios.fulfilled, (state, action) => {
                state.radios = action.payload;
                state.initialDataLoaded = true;
                state.radiosAndEdgesFetchedCount += 1;
            })
            .addCase(fetchEdges.fulfilled, (state, action) => {
                state.edges = action.payload;
                state.radiosAndEdgesFetchedCount += 1;
            })
            .addCase(fetchHardwares.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchHardwares.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchHardwares.rejected, (state) => {
                state.loading = false;
            });
    }
})
export const hardwareActions = hardwareSlice.actions;
export default hardwareSlice.reducer;
export const HardwareState = (state: RootState) => state.hardware.current
export const HardwareData = (state: RootState) => state.hardware.data
export const HardwareOpen = (state: RootState) => state.hardware.open
export const hardwareLoading = (state: RootState) => state.hardware.loading;

export const hardwareImportOpen = (state: RootState) => state.hardware.import.open;
export const hardwareImportData = (state: RootState) => state.hardware.import.data;
export const hardwareImportDataCount = (state: RootState) => state.hardware.import.dataCount;
export const hardwarePreserveSign = (state: RootState) => state.hardware.import.preserveSign;

export const hardwareImportFilteredData = (state: RootState) => state.hardware.import.filteredData;
export const hardwareImportFilteredDataCount = (state: RootState) => state.hardware.import.filteredDataCount;
export const hardwareRadios = (state: RootState) => state.hardware.radios;
export const hardwareEdges = (state: RootState) => state.hardware.edges;
export const hardwareEdgeCountsByServiceInstance = createSelector([hardwareEdges],
    (edges) => groupEdgeCounts(edges)
);
export const hardwareRadioCountsByServiceInstance = createSelector(
    [hardwareRadios],
    (radios) => groupRadioCounts(radios)
);
export const hardwareRadiosAndEdgesFetchedCount = (state: RootState) => state.hardware.radiosAndEdgesFetchedCount;
export const hardwareInitialDataLoaded = (state: RootState) => state.hardware.initialDataLoaded;

export interface HardwareInventoryVmcNode {
    id: string;
    name: string;
    key: string;
    isVMCPaused: string;
    usedCount?: number;
    assignedCount?: number;
}

export interface HardwareInventoryTenantNode {
    id: string;
    name: string;
    key: string;
    children: HardwareInventoryVmcNode[];
    usedCount?: number;
    assignedCount?: number;
}

export interface HardwareInventoryTree {
    id: string;
    name: string;
    children: HardwareInventoryTenantNode[];
    usedCount?: number;
    assignedCount?: number;
    unassignedCount?: number;
}

const EMPTY_HARDWARE_INVENTORY_TREE: HardwareInventoryTree = {
    id: "all",
    name: "All",
    children: [],
};

function buildHardwareInventoryBaseTree(serviceInstances: ServiceInstance[]): HardwareInventoryTree {
    if (!serviceInstances?.length) {
        return EMPTY_HARDWARE_INVENTORY_TREE;
    }

    const tenantMap = new Map<string, HardwareInventoryTenantNode>();
    serviceInstances.forEach((instance) => {
        if (!tenantMap.has(instance.tenantId)) {
            tenantMap.set(instance.tenantId, {
                id: instance.tenantId,
                name: instance.tenantName,
                key: instance.tenantId,
                children: [],
            });
        }

        tenantMap.get(instance.tenantId)?.children.push({
            id: instance.id,
            name: instance.name,
            key: instance.id,
            isVMCPaused: instance.pauseStatus ?? "",
        });
    });

    const sortedTenants = Array.from(tenantMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    sortedTenants.forEach((tenant) => {
        tenant.children.sort((a, b) => a.name.localeCompare(b.name));
    });

    return { id: "all", name: "All", children: sortedTenants };
}

function applyHardwareInventoryCounts(
    tree: HardwareInventoryTree,
    hardwares: HardwareX[],
    radios: Radio[],
): HardwareInventoryTree {
    if (tree.children.length === 0 || hardwares.length === 0) {
        return tree;
    }

    const radioIds = new Set(radios.map((radio) => radio.id).filter((id): id is string => Boolean(id)));
    const radioNames = new Set(radios.map((radio) => radio.name).filter((name): name is string => Boolean(name)));

    const isUsedHardware = (hardware: HardwareX) => {
        if (hardware.status !== "ASSIGNED" || !hardware.runtimeInfo?.linkedTo) {
            return false;
        }
        const linkedTo = hardware.runtimeInfo.linkedTo;
        // Store HardwareX may rewrite linkedTo to radio name; raw API data keeps radio id.
        return radioIds.has(linkedTo) || radioNames.has(linkedTo);
    };

    const children = tree.children.map((tenant) => {
        let tenantUsedCount = 0;
        let tenantAssignedCount = 0;

        const tenantChildren = tenant.children.map((serviceInstance) => {
            const serviceInstanceHardware = hardwares.filter(
                (hardware) => hardware.serviceInstanceId === serviceInstance.id,
            );
            const assignedCount = serviceInstanceHardware.filter((hardware) => hardware.status === "ASSIGNED").length;
            const usedCount = serviceInstanceHardware.filter(isUsedHardware).length;

            tenantUsedCount += usedCount;
            tenantAssignedCount += assignedCount - usedCount;

            return {
                ...serviceInstance,
                usedCount,
                assignedCount: assignedCount - usedCount,
            };
        });

        return {
            ...tenant,
            children: tenantChildren,
            usedCount: tenantUsedCount,
            assignedCount: tenantAssignedCount,
        };
    });

    return {
        ...tree,
        children,
        usedCount: children.reduce((sum, tenant) => sum + (tenant.usedCount ?? 0), 0),
        assignedCount: children.reduce((sum, tenant) => sum + (tenant.assignedCount ?? 0), 0),
        unassignedCount: hardwares.filter((hardware) => hardware.status === "UNASSIGNED").length,
    };
}

/** Tenant/VMC tree from service instances (no hardware counts). */
export const hardwareInventoryBaseTree = createSelector(
    [serviceInstanceData],
    (serviceInstances) => buildHardwareInventoryBaseTree(serviceInstances),
);

/**
 * Inventory sidebar tree with used/assigned/unassigned counts derived from
 * cached hardware + radios (avoids a separate tree count fetch).
 */
export const hardwareInventoryTree = createSelector(
    [hardwareInventoryBaseTree, HardwareData, hardwareRadios, hardwareInitialDataLoaded],
    (baseTree, hardwares, radios, radiosLoaded) => {
        // usedCount is derived by matching hardware against the radio fleet, so it is
        // only meaningful once the radios have arrived. Hardware and radios are fetched
        // independently and hardware usually wins, so without this the sidebar would
        // briefly report every assigned asset as unused (MCS-4316).
        if (!radiosLoaded) return baseTree;
        return applyHardwareInventoryCounts(baseTree, hardwares, radios);
    },
);

/** Tenant nodes only — shared by Assign / Revoke / Add Hardware dialogs. */
export const hardwareInventoryTreeTenants = createSelector(
    [hardwareInventoryTree],
    (tree) => tree.children,
);